import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getBlogPost } from '../graphql/queries';
import ReactMarkdown from 'react-markdown';
import './BlogPost.css';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { blogPosts } from '../data/blogposts';

const BlogPost = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const client = generateClient({ authMode: 'apiKey' });

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

  async function fetchBlogPost() {
    if (!id) {
      setError('No blog post ID provided');
      setLoading(false);
      return;
    }

    try {
      // First try to get from the database
      const blogPostData = await client.graphql({
        query: getBlogPost,
        variables: { id }
      });

      const fetchedPost = blogPostData.data.getBlogPost;

      if (fetchedPost) {
        setPost(fetchedPost);
      } else {
        // If not found in database, try to find in local data
        const localPost = blogPosts.find(post => post.id === id);
        if (localPost) {
          setPost(localPost);
        } else {
          setError('Blog post not found');
        }
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);

      // Try to find in local data
      const localPost = blogPosts.find(post => post.id === id);
      if (localPost) {
        setPost(localPost);
      } else {
        setError('An error occurred while fetching the blog post.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="blog-post__loading">Loading post...</div>;
  if (error) return <div className="blog-post__error">{error}</div>;
  if (!post) return <div className="blog-post__not-found">No blog post found.</div>;

  return (
    <div className="blog-post">
      <button
        onClick={() => navigate('/blog')}
        className="blog-post__back-button"
      >
        <ArrowLeft size={16} /> Back to Blog
      </button>

      <article className="blog-post__article">
        <header className="blog-post__header">
          <h1 className="blog-post__title">{post.title}</h1>
          <div className="blog-post__meta">
            <span className="blog-post__author">
              <User size={16} />
              {post.author}
            </span>
            <span className="blog-post__date">
              <Calendar size={16} />
              {new Date(post.publishDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </header>

        {post.svgContent && (
          <div
            className="blog-post__image"
            dangerouslySetInnerHTML={{ __html: post.svgContent }}
          />
        )}

        <div className="blog-post__content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
      </article>

      <div className="blog-post__footer">
        <hr className="blog-post__divider" />
        <p className="blog-post__footer-text">
          Interested in learning more about ChoiceRealms?
          <Link to="/about" className="blog-post__footer-link">Check out our about page</Link>
          or
          <Link to="/games" className="blog-post__footer-link">browse our game modules</Link>.
        </p>
      </div>
    </div>
  );
};

export default BlogPost;